/**
 * @module FleetManager
 * @description Main Fleet Manager page — lane allocation, dynamic tabs, and admin UI toggle.
 * Fetches lanes from DynamoDB via API, groups by store, filters by deployment stage tabs.
 * Provides allocate/release/extend for testers and CRUD via FleetAdminPanel for admins.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../aws-config';
import { Server, ArrowLeft, RefreshCw, ChevronDown, ChevronRight, Settings, RotateCcw, Package, DownloadCloud } from 'lucide-react';
import { STAGES, getLeaseDuration, laneWebUiUrl } from './fleetConstants';
import FleetTabs from './FleetTabs';
import FleetLaneCard from './FleetLaneCard';
import FleetDetailPanel from './FleetDetailPanel';
import CopyButton from './CopyButton';
import FleetAdminPanel from './FleetAdminPanel';
import DeployStatusSection from './DeployStatusSection';
import SyncFromDeviceModal from './SyncFromDeviceModal';
import { ModulesTable, useIssRestart, useIssModules } from './IssShared';

const API_BASE = awsConfig.API.REST.csvReviewApi.endpoint;

async function fleetApi(path, options = {}) {
  const session = await fetchAuthSession();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
  };
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

/**
 * Restart button + Active Modules toggle for an ISS in the store header.
 * Reuses the lane restart and lane modules endpoints (an ISS is stored as a
 * lane row) so ISS-only stores can be rebooted and inspected without a lane card.
 */
function IssControls({ iss, onRestart, onShowModules, actionLoading, onUpdateLane }) {
  const { restarting, message, restart } = useIssRestart(onRestart, iss);
  const [showSync, setShowSync] = useState(false);

  const handleSyncClick = (e) => {
    e.stopPropagation(); // don't toggle the store header collapse
    setShowSync(true);
  };

  const handleRestartClick = (e) => {
    e.stopPropagation(); // don't toggle the store header collapse
    restart();
  };

  const handleModulesClick = (e) => {
    e.stopPropagation(); // don't toggle the store header collapse
    if (onShowModules) onShowModules(iss);
  };

  return (
    <>
      {iss.dsn && onUpdateLane && (
        <button
          type="button"
          className="fleet-restart-btn fleet-iss-sync-btn"
          onClick={handleSyncClick}
          disabled={iss.status === 'IN_USE'}
          title={iss.status === 'IN_USE'
            ? 'Release the ISS before syncing — an in-use lane cannot be edited'
            : 'Read this ISS via SSM and update its Device ID and IP if they have drifted'}
          aria-label="Sync ISS from device"
        >
          <DownloadCloud size={12} />
          <span>Sync from device</span>
        </button>
      )}
      <button
        type="button"
        className="fleet-restart-btn fleet-iss-restart-btn"
        onClick={handleRestartClick}
        disabled={restarting || actionLoading}
        title={restarting ? 'Sending reboot…' : 'Reboot the ISS'}
        aria-label="Restart ISS"
      >
        <RotateCcw size={12} className={restarting ? 'fleet-restart-spinning' : ''} />
        <span>{restarting ? 'Rebooting…' : 'Restart'}</span>
      </button>
      <button
        type="button"
        className="fleet-restart-btn fleet-iss-modules-btn"
        onClick={handleModulesClick}
        title="View ISS active modules"
        aria-label="ISS active modules"
      >
        <Package size={12} />
        <span>Active Modules</span>
      </button>
      {message && (
        <span className={`fleet-iss-restart-msg ${message.type === 'error' ? 'fleet-restart-error' : 'fleet-restart-success'}`}>
          {message.type === 'error' ? '⚠ ' : '🔄 '}{message.text}
        </span>
      )}
      {showSync && (
        <SyncFromDeviceModal
          lane={iss}
          fleetApi={fleetApi}
          onApply={onUpdateLane}
          onClose={() => setShowSync(false)}
        />
      )}
    </>
  );
}

/**
 * Right-side panel showing an ISS's active module set, styled like the lane
 * detail panel. Fetches modules from the ISS device (reuses the lane modules
 * endpoint, since an ISS is stored as a lane row with its own DSN).
 */
function IssModulesPanel({ iss, onRestart, onClose, actionLoading, deployWindowOpen, onUpdateLane }) {
  const [showSync, setShowSync] = useState(false);
  const issWebUiUrl = laneWebUiUrl(iss);
  const { restarting, message: restartMessage, restart: handleRestart } = useIssRestart(onRestart, iss);
  const {
    modules, expectedModules, eleraVersion, osVersion,
    loading: modulesLoading, error: modulesError,
  } = useIssModules(iss?.laneId, iss?.dsn, fleetApi);

  return (
    <div className="fleet-detail-panel">
      <button className="fleet-detail-close" onClick={onClose}>
        <ArrowLeft size={16} /> Back
      </button>
      <div className="fleet-detail-title-row">
        <h3>{iss.hardwareId || iss.laneId} — ISS</h3>
        {iss.dsn && onUpdateLane && (
          <button
            className="fleet-restart-btn"
            onClick={() => setShowSync(true)}
            disabled={iss.status === 'IN_USE'}
            title={iss.status === 'IN_USE'
              ? 'Release the ISS before syncing — an in-use lane cannot be edited'
              : 'Read this ISS via SSM and update its Device ID and IP if they have drifted'}
            aria-label="Sync ISS from device"
          >
            <DownloadCloud size={14} />
            <span>Sync from device</span>
          </button>
        )}
        {iss.dsn && onRestart && (
          <button
            className="fleet-restart-btn"
            onClick={handleRestart}
            disabled={restarting || actionLoading}
            title={restarting ? 'Sending reboot…' : 'Reboot the ISS'}
            aria-label="Restart ISS"
          >
            <RotateCcw size={14} className={restarting ? 'fleet-restart-spinning' : ''} />
            <span>{restarting ? 'Rebooting…' : 'Reboot the ISS'}</span>
          </button>
        )}
      </div>
      {showSync && (
        <SyncFromDeviceModal
          lane={iss}
          fleetApi={fleetApi}
          onApply={onUpdateLane}
          onClose={() => setShowSync(false)}
        />
      )}
      {restartMessage && (
        <div className={`fleet-restart-message ${restartMessage.type === 'error' ? 'fleet-restart-error' : 'fleet-restart-success'}`}>
          {restartMessage.type === 'error' ? '⚠ ' : '🔄 '}{restartMessage.text}
        </div>
      )}
      <table className="fleet-detail-table fleet-iss-detail-table">
        <tbody>
          <tr><td>Stage</td><td>{iss.deploymentStage || '—'}</td></tr>
          <tr><td>Store ID</td><td>{iss.storeId || '—'}</td></tr>
          <tr><td>IP Address</td><td>{iss.ipAddress || '—'}</td></tr>
          <tr><td>Port</td><td>{iss.port || '—'}</td></tr>
          <tr><td>DSN</td><td>{iss.dsn || '—'}</td></tr>
          {issWebUiUrl && (
            <tr><td>Web UI</td><td><a href={issWebUiUrl} target="_blank" rel="noreferrer">{issWebUiUrl}</a></td></tr>
          )}
        </tbody>
      </table>

      <DeployStatusSection laneId={iss.laneId} dsn={iss.dsn} open={deployWindowOpen} fleetApi={fleetApi} />

      <div className="fleet-detail-modules-heading">
        <Package size={14} />
        <span>ISS Active Modules</span>
      </div>
      <div className="fleet-modules-section">
        {modulesLoading && (
          <div className="fleet-modules-loading">
            <span className="fleet-modules-spinner" />
            <div>
              <div>Connecting to ISS via SSM...</div>
              <div className="fleet-modules-loading-hint">This may take 5–10 seconds</div>
            </div>
          </div>
        )}
        {modulesError && (
          <div className="fleet-modules-error">⚠ {modulesError}<div className="fleet-modules-error-hint">The ISS may be offline or unreachable. Try again later.</div></div>
        )}
        {!modulesLoading && eleraVersion && (
          <div className="fleet-platform-version">Elera Version: <span className="fleet-version-value">{eleraVersion}</span><CopyButton value={eleraVersion} /></div>
        )}
        {!modulesLoading && osVersion && (
          <div className="fleet-platform-version">Majestic OS Version: <span className="fleet-version-value">{osVersion}</span><CopyButton value={osVersion} /></div>
        )}
        {!modulesLoading && (
          <ModulesTable
            modules={modules}
            expectedModules={expectedModules}
            stage={iss.deploymentStage}
            emptyMessage="No modules found on the ISS."
          />
        )}
      </div>
    </div>
  );
}

function FleetManager() {
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLane, setSelectedLane] = useState(null);
  const [issModulesPanelIss, setIssModulesPanelIss] = useState(null);
  const [selectedLanePerTab, setSelectedLanePerTab] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState('');
  const [, setTick] = useState(0);
  const [expandedStores, setExpandedStores] = useState({});
  const [activeTab, setActiveTab] = useState(STAGES.BETA.key);
  const [showAdmin, setShowAdmin] = useState(false);
  const [deployWindows, setDeployWindows] = useState({});
  const releasingRef = useRef(new Set());
  const initializedRef = useRef(false);

  const fetchLanes = useCallback(async () => {
    try {
      const data = await fleetApi('/fleet/lanes');
      const fetchedLanes = data.lanes || [];
      setLanes(fetchedLanes);
      if (!initializedRef.current && fetchedLanes.length > 0) {
        initializedRef.current = true;
        const storeIds = [...new Set(fetchedLanes.map(l => l.storeId))];
        setExpandedStores(Object.fromEntries(storeIds.map(id => [id, true])));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLanes();
    fetchAuthSession().then(s => {
      setCurrentUser(s.tokens?.idToken?.payload?.email?.split('@')[0] || '');
    });
    const interval = setInterval(fetchLanes, 30000);
    return () => clearInterval(interval);
  }, [fetchLanes]);

  // Poll pipeline health for the per-stage deploy-status window. When a
  // RISDeployment-* pipeline is mid-deploy, the backend opens a ~4h window
  // (deployWindows[stage]) during which lanes show live deploy status.
  useEffect(() => {
    let cancelled = false;
    const fetchDeployWindows = async () => {
      try {
        const data = await fleetApi('/pipelines/health');
        if (!cancelled) setDeployWindows(data.deployWindows || {});
      } catch {
        // Non-fatal — deploy status is a bonus overlay; leave windows as-is.
      }
    };
    fetchDeployWindows();
    const interval = setInterval(fetchDeployWindows, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Auto-release expired lanes owned by current user
  useEffect(() => {
    const timer = setInterval(() => {
      setTick(t => t + 1);
      lanes.filter(l => l.status === 'IN_USE' && l.allocatedAt &&
        (l.allocatedBy === currentUser || l.allocatedBy === currentUser + '@amazon.com')
      ).forEach(lane => {
        const remaining = getLeaseDuration(lane) - (Date.now() - new Date(lane.allocatedAt).getTime());
        if (remaining <= 0 && !releasingRef.current.has(lane.laneId)) {
          releasingRef.current.add(lane.laneId);
          fleetApi(`/fleet/lanes/${lane.laneId}/release`, { method: 'POST' })
            .then(() => fetchLanes())
            .catch(err => console.warn(`Auto-release failed for ${lane.laneId}:`, err))
            .finally(() => releasingRef.current.delete(lane.laneId));
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lanes, currentUser, fetchLanes]);

  const handleAllocate = async (laneId, purpose, durationSeconds) => {
    setActionLoading(true);
    setError('');
    try {
      await fleetApi(`/fleet/lanes/${laneId}/allocate`, {
        method: 'POST',
        body: JSON.stringify({ purpose, duration: durationSeconds })
      });
      await fetchLanes();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRelease = async (laneId) => {
    setActionLoading(true);
    setError('');
    try {
      await fleetApi(`/fleet/lanes/${laneId}/release`, { method: 'POST' });
      await fetchLanes();
      setSelectedLane(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Memoized so its identity is stable across renders — otherwise the 1s tick
  // re-creates it every second and re-fires the detail panel's reachability probe.
  const handleRestart = useCallback(async (laneId) => {
    setActionLoading(true);
    setError('');
    try {
      const result = await fleetApi(`/fleet/lanes/${laneId}/restart`, { method: 'POST' });
      await fetchLanes();
      return result;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setActionLoading(false);
    }
  }, [fetchLanes]);

  const handleExtend = async (laneId, durationSeconds) => {
    setActionLoading(true);
    setError('');
    try {
      await fleetApi(`/fleet/lanes/${laneId}/extend`, { method: 'POST', body: JSON.stringify({ durationSeconds }) });
      await fetchLanes();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateLane = async (laneData) => {
    setActionLoading(true);
    try {
      await fleetApi('/fleet/lanes', { method: 'POST', body: JSON.stringify(laneData) });
      await fetchLanes();
    } catch (e) {
      throw e;
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Memoized so its identity is stable across renders — otherwise the 1s tick
   * re-creates it every second, which (via the onApply prop) would re-run the
   * Sync modal's auto-sync effect. Mirrors handleRestart above.
   */
  const handleUpdateLane = useCallback(async (laneId, laneData) => {
    setActionLoading(true);
    try {
      await fleetApi(`/fleet/lanes/${laneId}`, { method: 'PUT', body: JSON.stringify(laneData) });
      await fetchLanes();
    } catch (e) {
      throw e;
    } finally {
      setActionLoading(false);
    }
  }, [fetchLanes]);

  const handleDeleteLane = async (laneId) => {
    setActionLoading(true);
    try {
      await fleetApi(`/fleet/lanes/${laneId}`, { method: 'DELETE' });
      await fetchLanes();
    } catch (e) {
      throw e;
    } finally {
      setActionLoading(false);
    }
  };

  // Handle tab switching with per-tab lane memory
  const handleTabSwitch = (targetTab) => {
    if (targetTab === activeTab) return;
    setSelectedLanePerTab(prev => ({ ...prev, [activeTab]: selectedLane }));
    setActiveTab(targetTab);

    const saved = selectedLanePerTab[targetTab];
    const fresh = saved ? lanes.find(l => l.laneId === saved.laneId) : null;
    if (fresh && fresh.status === 'IN_USE') {
      setSelectedLane(fresh);
    } else {
      const myLane = lanes.find(
        l => l.deploymentStage === targetTab &&
             l.status === 'IN_USE' &&
             (l.allocatedBy === currentUser || l.allocatedBy === currentUser + '@amazon.com')
      );
      setSelectedLane(myLane || null);
    }
  };

  // Filter lanes by active tab
  const filteredLanes = useMemo(() =>
    activeTab === 'All' ? lanes : lanes.filter(l => l.deploymentStage === activeTab),
    [lanes, activeTab]
  );

  // Group lanes by store
  const grouped = filteredLanes.reduce((acc, lane) => {
    const key = lane.storeId || 'Unknown';
    if (!acc[key]) acc[key] = { iss: null, lanes: [], label: lane.storeLabel || key, enterpriseUrl: lane.enterpriseUrl };
    if (lane.laneType === 'ISS') acc[key].iss = lane;
    else acc[key].lanes.push(lane);
    return acc;
  }, {});

  /**
   * Explicit store display order per tab. Beta stores first, then the gamma
   * stores in Gamma-tab order: the two Austin gamma stores, then US-MA-21
   * (Bangalore), then the Gamma NFT group (US-TX-55-NFT) last. Stores not
   * listed fall to the end (999) in their existing relative order.
   */
  const storeOrder = ['10145', '10252', '10071', '10438', 'US-TX-55', 'US-IL-19', 'US-MA-21', 'US-TX-55-NFT'];
  const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
    const ai = storeOrder.indexOf(a), bi = storeOrder.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // Update selectedLane from fresh data
  useEffect(() => {
    setSelectedLane(prev => {
      if (!prev) return prev;
      const fresh = lanes.find(l => l.laneId === prev.laneId);
      return fresh || null;
    });
    setIssModulesPanelIss(prev => {
      if (!prev) return prev;
      const fresh = lanes.find(l => l.laneId === prev.laneId);
      return fresh || null;
    });
  }, [lanes]);

  const freeLanes = useMemo(() => filteredLanes.filter(l => l.status === 'FREE' && l.laneType !== 'ISS').length, [filteredLanes]);
  const inUseLanes = useMemo(() => filteredLanes.filter(l => l.status === 'IN_USE' && l.laneType !== 'ISS').length, [filteredLanes]);
  const totalLanes = useMemo(() => filteredLanes.filter(l => l.laneType !== 'ISS').length, [filteredLanes]);

  // Known stage colors — new stages get a default color
  const DEFAULT_TAB_COLOR = '#455a64';

  // Derive tabs dynamically from lane data
  const dynamicStages = useMemo(() => {
    const stagesByKey = Object.fromEntries(
      Object.values(STAGES).map(s => [s.key, s])
    );
    const fromData = [...new Set(lanes.map(l => l.deploymentStage).filter(Boolean))];
    const allKeys = [...new Set([...Object.keys(stagesByKey), ...fromData])];
    return allKeys.map(key => ({
      key,
      label: stagesByKey[key]?.label || key.replace('Hawkeye ', ''),
      color: stagesByKey[key]?.color || DEFAULT_TAB_COLOR,
    }));
  }, [lanes]);

  const tabCounts = useMemo(() => {
    const counts = {};
    dynamicStages.forEach(stage => {
      counts[stage.key] = lanes.filter(l => l.deploymentStage === stage.key && l.laneType !== 'ISS').length;
    });
    return counts;
  }, [lanes, dynamicStages]);

  // ISS lane for the selected lane's store
  const storeIss = selectedLane ? lanes.find(l => l.storeId === selectedLane.storeId && l.laneType === 'ISS') : null;

  // Whether the deploy-status window is open for a lane's stage (beta/gamma).
  const isDeployWindowOpen = (stage) => {
    const key = (stage || '').trim().toLowerCase();
    const w = deployWindows[key];
    return !!(w && w.until && new Date(w.until).getTime() > Date.now());
  };

  if (loading) return <div className="loading">Loading fleet data...</div>;

  return (
    <div className="fleet-manager">
      <div className="fleet-header">
        <div>
          <h2>Fleet Manager</h2>
          <p className="fleet-subtitle">QA Lab Lane Allocation — click a lane to allocate or release</p>
          {!showAdmin && (
            <div className="fleet-manage-row">
              <span className="fleet-manage-hint">Add, Edit, or Remove Lanes for Your Fleet</span>
              <button className="fleet-manage-btn" onClick={() => setShowAdmin(true)}>
                <Settings size={13} /> Manage Lanes
              </button>
            </div>
          )}
        </div>
        <div className="fleet-summary">
          <span className="fleet-stat fleet-stat-free">{freeLanes} Free</span>
          <span className="fleet-stat fleet-stat-busy">{inUseLanes} In Use</span>
          <span className="fleet-stat fleet-stat-total">{totalLanes} Total</span>
          <button className="btn btn-sm btn-secondary" onClick={fetchLanes} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && <div className="fleet-error">{error} <button onClick={() => setError('')}>×</button></div>}

      {showAdmin ? (
        <>
          <button className="btn btn-secondary btn-sm fleet-back-to-lanes" onClick={() => setShowAdmin(false)}>
            <ArrowLeft size={14} /> Back to Lane View
          </button>
          <FleetAdminPanel
            lanes={lanes}
            onCreateLane={handleCreateLane}
            onUpdateLane={handleUpdateLane}
            onDeleteLane={handleDeleteLane}
            actionLoading={actionLoading}
          />
        </>
      ) : (
      <>
      <FleetTabs activeTab={activeTab} onTabSwitch={handleTabSwitch} tabCounts={tabCounts} stages={dynamicStages} />
      <div className="fleet-content">
        <div className="fleet-grid">
          {sortedGroups.map(([storeId, group]) => (
            <div key={storeId} className="fleet-store-group">
              <div className="fleet-store-header" onClick={() => setExpandedStores(prev => ({ ...prev, [storeId]: !prev[storeId] }))}>
                {expandedStores[storeId] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <Server size={16} />
                <span>{group.label}</span>
                <span className="fleet-store-count">{group.lanes.filter(l => l.status === 'IN_USE').length}/{group.lanes.length} in use</span>
              </div>
              {group.iss && (
                <div className="fleet-store-info">
                  <div className="fleet-info-row">
                    <span className="fleet-info-label">ISS:</span>
                    <a href={laneWebUiUrl(group.iss)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{group.iss.ipAddress}:{group.iss.port}</a>
                    {group.iss.dsn && isDeployWindowOpen(group.iss.deploymentStage) && (
                      <span className="lane-card-deploy-pill" title="Deployment in progress — open Active Modules to see status">🚀 deploy</span>
                    )}
                    {group.iss.dsn && (
                      <IssControls
                        iss={group.iss}
                        onRestart={handleRestart}
                        onShowModules={(iss) => { setSelectedLane(null); setIssModulesPanelIss(iss); }}
                        actionLoading={actionLoading}
                        onUpdateLane={handleUpdateLane}
                      />
                    )}
                  </div>
                </div>
              )}
              {expandedStores[storeId] && (
                <>
                  <div className="fleet-lane-cards">
                    {group.lanes.filter(l => l.laneType === 'POS').length > 0 && (
                      <>
                        <div className="fleet-lane-type-label">POS</div>
                        <div className="fleet-lane-row">
                          {group.lanes.filter(l => l.laneType === 'POS').sort((a, b) => a.hardwareId.localeCompare(b.hardwareId)).map(lane => (
                            <FleetLaneCard key={lane.laneId} lane={lane} isSelected={selectedLane?.laneId === lane.laneId} onSelect={(l) => { setIssModulesPanelIss(null); setSelectedLane(l); }} deployWindowOpen={isDeployWindowOpen(lane.deploymentStage)} />
                          ))}
                        </div>
                      </>
                    )}
                    {group.lanes.filter(l => l.laneType === 'SCO').length > 0 && (
                      <>
                        <div className="fleet-lane-type-label">SCO</div>
                        <div className="fleet-lane-row">
                          {group.lanes.filter(l => l.laneType === 'SCO').sort((a, b) => a.hardwareId.localeCompare(b.hardwareId)).map(lane => (
                            <FleetLaneCard key={lane.laneId} lane={lane} isSelected={selectedLane?.laneId === lane.laneId} onSelect={(l) => { setIssModulesPanelIss(null); setSelectedLane(l); }} deployWindowOpen={isDeployWindowOpen(lane.deploymentStage)} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {selectedLane && (
          <FleetDetailPanel
            selectedLane={selectedLane}
            storeIss={storeIss}
            deployWindowOpen={isDeployWindowOpen(selectedLane.deploymentStage)}
            currentUser={currentUser}
            onRelease={handleRelease}
            onAllocate={handleAllocate}
            onExtend={handleExtend}
            onRestart={handleRestart}
            onClose={() => setSelectedLane(null)}
            actionLoading={actionLoading}
            fleetApi={fleetApi}
            onUpdateLane={handleUpdateLane}
          />
        )}

        {issModulesPanelIss && !selectedLane && (
          <IssModulesPanel
            iss={issModulesPanelIss}
            deployWindowOpen={isDeployWindowOpen(issModulesPanelIss.deploymentStage)}
            onRestart={handleRestart}
            onClose={() => setIssModulesPanelIss(null)}
            actionLoading={actionLoading}
            onUpdateLane={handleUpdateLane}
          />
        )}
      </div>
      </>
      )}
    </div>
  );
}

export default FleetManager;
